import React, { useEffect, useState } from 'react';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import SlugLink from '../../../slug-link';
import { BlogTopicFragment } from '../../../../__generated__/gatsby-types';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useIsMobile } from '../../../../util/style-utils';
import slugify from 'slugify';
import PgeButton from '../../../../components/pge-button/_PgeButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.spacing(1.75),
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    tag: {
      fontSize: theme.spacing(1.75),
      margin: '0.25rem 0.25rem 0.25rem 0',
      whiteSpace: 'nowrap',
      display: 'inline-block',
      borderRadius: '40px',
      textDecoration: 'none',
      fontWeight: 600,
      padding: '10px',
      verticalAlign: 'middle',
      transition: 'all 250ms ease',
      backgroundColor: colors.riverBlue,
      color: colors.sparkBlue,
    },
    tagList: {
      [theme.breakpoints.up('md')]: {
        margin: '13px 0 0',
      },
      [theme.breakpoints.down('sm')]: {
        margin: '20px 0',
      },
    },
    showMoreButton: {
      fontSize: '16px',
      [theme.breakpoints.up('md')]: {
        padding: '15px 10px 10px 10px',
      },
      [theme.breakpoints.down('sm')]: {
        padding: '0 10px 10px 10px',
      },
    },
  }),
);

type Props = {
  topics: BlogTopicFragment[];
  currentTopicId?: string;
  title?: string;
};

/**
 * Filters out duplicate topics based on their `contentful_id`.
 * Ensures that `currentTopicId`, if provided, is excluded from the result.
 */
const filterTopics = (
  currentTopicId: string | undefined,
  topics: BlogTopicFragment[],
): BlogTopicFragment[] => {
  const filteredTopics = new Set<string>(
    currentTopicId ? [currentTopicId] : [],
  );

  return topics.filter(topic => {
    if (filteredTopics.has(topic.contentful_id)) {
      return false;
    }
    filteredTopics.add(topic.contentful_id);
    return true;
  });
};

export const RelatedTopics = (props: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const classes = useStyles({ isMobile });
  const { topics, currentTopicId, title = t('TOPICS') } = props;

  const [topicsToShow, setTopicsToShow] = useState<BlogTopicFragment[] | null>(
    null,
  );

  const [isShowAll, setIsShowAll] = useState(false);

  const filteredTopics = filterTopics(currentTopicId, topics);

  useEffect(() => {
    setTopicsToShow(isShowAll ? filteredTopics : filteredTopics.slice(0, 9));
  }, [isShowAll, topics]);

  if (topicsToShow && topicsToShow.length > 0) {
    return (
      <>
        <Typography variant={'body1'} className={classes.heading}>
          {title}
        </Typography>
        <Box className={classes.tagList}>
          {topicsToShow?.map((topic, index) => {
            return (
              <SlugLink
                key={`${topic.topic}${index}`}
                className={classes.tag}
                slug={`topic-${slugify(topic.slug!, {
                  lower: true,
                  remove: /[^\w\s.{}\/\-]+/g,
                })}`}
                linkText={topic.topic}
              />
            );
          })}
        </Box>
        {!isShowAll && topicsToShow?.length < filteredTopics?.length && (
          <PgeButton
            theme="Text"
            onClick={() => setIsShowAll(value => !value)}
            className={classes.showMoreButton}
          >
            {t('SEE_MORE_TOPICS')}
          </PgeButton>
        )}
      </>
    );
  }
  return null;
};
export default RelatedTopics;
